/* ====================================================================
   VERDICT GOODS — vg-core.js (ported from source verdict.js)
   Shared behavior layer: loader, countdowns, topbar collapse/tuck,
   bite-notch button geometry, marquees, scramble text.
   Theme additions: idempotency guards + Shopify theme-editor lifecycle
   (shopify:section:load re-inits without duplicating listeners/timers).
   ==================================================================== */
window.Verdict = (function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- wheel-path debug badge (?vgdebug=1) ----
     Counts wheel input reaching the scroll engine WITHOUT registering any
     DOM wheel listener (listening could alter event routing and mask the
     bug); reads the engine's own virtual-scroll emitter instead. */
  function initWheelDebug() {
    if (location.search.indexOf('vgdebug') === -1 || document.getElementById('vg-wheel-debug')) return;
    var b = document.createElement('div');
    b.id = 'vg-wheel-debug';
    b.style.cssText = 'position:fixed;left:10px;bottom:10px;z-index:2147483647;background:#33302A;color:#F5EEDA;font:13px/1.6 monospace;padding:6px 10px;border-radius:4px;pointer-events:none';
    document.body.appendChild(b);
    var input = 0, moved = 0, hooked = false;
    (function tick() {
      if (!hooked && window.VG_LENIS) {
        hooked = true;
        window.VG_LENIS.on('virtual-scroll', function () { input++; });
        window.VG_LENIS.on('scroll', function () { moved++; });
      }
      b.textContent = (hooked ? '' : 'no engine · ') + 'input ' + input + ' · move ' + moved + ' · y ' + Math.round(window.scrollY);
      requestAnimationFrame(tick);
    })();
  }

  /* ---- loader (auto: runs if #loader exists) ---- */
  function initLoader() {
    var loader = document.getElementById('loader');
    if (!loader || loader.dataset.vgInit) return;
    loader.dataset.vgInit = '1';
    var s0 = null;
    document.body.style.overflow = reduce ? '' : 'hidden';
    function tick(t) {
      if (!s0) s0 = t;
      var p = Math.min(1, (t - s0) / 1500);
      if (p < 1) requestAnimationFrame(tick);
      else setTimeout(function () { loader.classList.add('done'); document.body.style.overflow = ''; }, 260);
    }
    if (reduce) { loader.classList.add('done'); document.body.style.overflow = ''; }
    else requestAnimationFrame(tick);
    /* hard ceiling — a background tab suspends rAF; never strand the page */
    setTimeout(function () { loader.classList.add('done'); document.body.style.overflow = ''; }, 6000);
  }

  /* ---- scramble headline(s) on load: any [data-scramble] ---- */
  function initScramble(scope) {
    (scope || document).querySelectorAll('[data-scramble]').forEach(function (el) {
      if (reduce || el.dataset.vgScrambled) return;
      el.dataset.vgScrambled = '1';
      var full = el.textContent, chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ.', frame = 0;
      function run() {
        var out = '';
        for (var i = 0; i < full.length; i++) {
          if (full[i] === ' ' || full[i] === '\n') { out += full[i]; continue; }
          if (i < frame / 2) { out += full[i]; }
          else {
            out += full[i] === full[i].toLowerCase() && full[i] !== full[i].toUpperCase()
              ? chars[Math.floor(Math.random() * chars.length)].toLowerCase()
              : chars[Math.floor(Math.random() * chars.length)];
          }
        }
        el.textContent = out; frame++;
        if (frame / 2 < full.length) setTimeout(run, 26); else el.textContent = full;
      }
      run();
    });
  }

  /* ---- reveal + slam: any .reveal / .slam ---- */
  var revealIO = null;
  function initReveals(scope) {
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); revealIO.unobserve(e.target); } });
      }, { threshold: .16, rootMargin: '0px 0px -8% 0px' });
    }
    (scope || document).querySelectorAll('.reveal,.slam').forEach(function (el) {
      if (el.dataset.vgRevealed) return;
      el.dataset.vgRevealed = '1';
      revealIO.observe(el);
    });
  }

  /* ---- signature draw: [data-draw-on-view] svg with a <path> ---- */
  function initSignatureDraw(scope) {
    (scope || document).querySelectorAll('[data-draw-on-view]').forEach(function (sig) {
      if (sig.dataset.vgDraw) return;
      sig.dataset.vgDraw = '1';
      var sio = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { sig.classList.add('draw'); sio.disconnect(); } });
      }, { threshold: .4 });
      sio.observe(sig);
    });
  }

  /* ---- fade/scale-in images on load: any .fade-in-img ---- */
  function initFadeImages(scope) {
    if (reduce) return;
    (scope || document).querySelectorAll('.fade-in-img').forEach(function (img, i) {
      if (img.dataset.vgFaded) return;
      img.dataset.vgFaded = '1';
      img.animate([{ opacity: 0, transform: 'scale(1.06)' }, { opacity: 1, transform: 'none' }],
        { duration: 900, delay: 400 + i * 120, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' });
    });
  }

  /* ---- topbar: open at top -> monogram square past 60px -> peek on hover/focus;
     tucks out of the corner while scrolling down, returns on scroll up ---- */
  function initTopbarScroll() {
    var topbar = document.getElementById('topbar');
    if (!topbar || topbar.dataset.vgInit) return;
    topbar.dataset.vgInit = '1';
    var bar = topbar.querySelector('.tb-bar');
    var past = false, peek = false, lastY = window.scrollY, hidden = false;
    function sync() { topbar.classList.toggle('shut', past && !peek); }
    function onScroll() {
      var y = window.scrollY;
      topbar.classList.toggle('scrolled', y > 40);
      past = y > 60;
      var down = y > lastY + 2, up = y < lastY - 2;
      if (down && past && !peek && !hidden) { hidden = true; }
      else if ((up || !past) && hidden) { hidden = false; }
      if (Math.abs(y - lastY) > 2) lastY = y;
      topbar.classList.toggle('tucked', hidden);
      sync();
    }
    if (bar) {
      bar.addEventListener('mouseenter', function () { peek = true; sync(); });
      bar.addEventListener('mouseleave', function () { peek = false; sync(); });
      bar.addEventListener('focusin', function () { peek = true; sync(); });
      bar.addEventListener('focusout', function () { peek = false; sync(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- real cookie-bite button geometry (Signature/Drop Button, viewBox 248x42) ---- */
  function bitePath(W, H) {
    var dx = W - 247.5;
    return 'M' + (225.035 + dx) + ' 4.47534C' + (221.862 + dx) + ' 4.47534 ' + (219.175 + dx) + ' 2.59375 ' + (218.272 + dx) + ' 0H0V' + H + 'H' + W + 'V9.58472C' + (246.51 + dx) + ' 10.0446 ' + (245.386 + dx) + ' 10.3042 ' + (244.196 + dx) + ' 10.3042C' + (242.838 + dx) + ' 10.3042 ' + (241.568 + dx) + ' 9.96655 ' + (240.482 + dx) + ' 9.37915C' + (239.026 + dx) + ' 11.5494 ' + (236.382 + dx) + ' 13 ' + (233.364 + dx) + ' 13C' + (228.772 + dx) + ' 13 ' + (225.049 + dx) + ' 9.64209 ' + (225.049 + dx) + ' 5.5C' + (225.049 + dx) + ' 5.15222 ' + (225.075 + dx) + ' 4.81006 ' + (225.126 + dx) + ' 4.47485L' + (225.035 + dx) + ' 4.47534Z';
  }
  function flatPath(W, H) {
    var c = W + ' 0 ' + W + ' 0 ' + W + ' 0';
    return 'M' + W + ' 0C' + c + 'H0V' + H + 'H' + W + 'V0C' + c + 'C' + c + 'C' + c + 'C' + c + 'C' + c + 'L' + W + ' 0Z';
  }
  function fitBite(el) {
    var W = Math.round(el.offsetWidth), H = Math.round(el.offsetHeight);
    if (!W || !H) return;
    el.style.setProperty('--flat', 'path("' + flatPath(W, H) + '")');
    el.style.setProperty('--bite', 'path("' + bitePath(W, H) + '")');
  }
  var biteRO = null, biteSeen = null;
  function initBiteButtons() {
    function fitAll() { document.querySelectorAll('.drop').forEach(fitBite); }
    fitAll();
    if (document.body.dataset.vgBite) return;
    document.body.dataset.vgBite = '1';
    window.addEventListener('resize', fitAll);
    if (document.fonts) document.fonts.ready.then(fitAll);
    if (!window.ResizeObserver) return;
    biteRO = new ResizeObserver(function (entries) { entries.forEach(function (e) { fitBite(e.target); }); });
    biteSeen = new WeakSet();
    function observeAll() {
      document.querySelectorAll('.drop').forEach(function (b) {
        if (biteSeen.has(b)) return;
        biteSeen.add(b);
        biteRO.observe(b);
      });
    }
    observeAll();
    new MutationObserver(observeAll).observe(document.body, { childList: true, subtree: true });
  }

  /* ---- countdown: <el data-countdown-epoch="unix seconds"> containing [data-u=d/h/m/s].
     The epoch is computed server-side in the store's timezone (vg-countdown.liquid),
     so the target moment is identical for every visitor. data-countdown-target
     (a raw date string) is only a legacy fallback. ---- */
  function pad(n) { return String(n).padStart(2, '0'); }
  function initCountdowns(scope) {
    (scope || document).querySelectorAll('[data-countdown-epoch],[data-countdown-target]').forEach(function (clockEl) {
      if (clockEl.dataset.vgTicking) return;
      clockEl.dataset.vgTicking = '1';
      var epoch = parseInt(clockEl.getAttribute('data-countdown-epoch'), 10);
      var target = isNaN(epoch)
        ? new Date(clockEl.getAttribute('data-countdown-target')).getTime()
        : epoch * 1000;
      if (isNaN(target)) return;
      function upd() {
        var diff = Math.max(0, target - Date.now());
        var d = Math.floor(diff / 864e5), h = Math.floor(diff % 864e5 / 36e5), m = Math.floor(diff % 36e5 / 6e4), s = Math.floor(diff % 6e4 / 1e3);
        var dEl = clockEl.querySelector('[data-u=d]'), hEl = clockEl.querySelector('[data-u=h]'),
          mEl = clockEl.querySelector('[data-u=m]'), sEl = clockEl.querySelector('[data-u=s]');
        if (dEl) dEl.textContent = pad(d); if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m); if (sEl) sEl.textContent = pad(s);
      }
      upd();
      var iv = setInterval(function () {
        if (!document.contains(clockEl)) { clearInterval(iv); return; } /* section reloaded away */
        upd();
      }, 1000);
    });
  }

  /* ---- marquee: seamless infinite, two identical halves wider than the container ---- */
  function marquee(items, trackTarget) {
    var track = typeof trackTarget === 'string' ? document.querySelector(trackTarget) : trackTarget;
    if (!track) return;
    function unit() {
      var s = '';
      items.forEach(function (it) {
        s += it === 'star'
          ? '<span class="item star"><svg viewBox="0 0 62 70" style="fill:currentColor"><use href="#star"/></svg></span>'
          : '<span class="item">' + it + '</span>';
      });
      return s;
    }
    track.innerHTML = unit();
    var unitW = track.scrollWidth || 1;
    var contW = (track.parentElement ? track.parentElement.clientWidth : unitW);
    var perHalf = Math.max(1, Math.ceil(contW / unitW) + 1);
    var half = ''; for (var i = 0; i < perHalf; i++) { half += unit(); }
    track.innerHTML = half + half;
    var halfW = track.scrollWidth / 2;
    track.style.animationDuration = Math.max(18, Math.round(halfW / 70)) + 's';
  }
  function initMarqueesFromMarkup(scope) {
    var tracks = [].slice.call((scope || document).querySelectorAll('[data-marquee-items]'));
    function build() {
      tracks.forEach(function (track) {
        try { marquee(JSON.parse(track.getAttribute('data-marquee-items')), track); } catch (e) { /* malformed setting — leave static */ }
      });
    }
    build();
    if (!document.body.dataset.vgMarqueeResize) {
      document.body.dataset.vgMarqueeResize = '1';
      var t; window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          document.querySelectorAll('[data-marquee-items]').forEach(function (track) {
            try { marquee(JSON.parse(track.getAttribute('data-marquee-items')), track); } catch (e) {}
          });
        }, 200);
      });
    }
  }

  /* ---- drop-state body classes stay truthful across section re-renders ----
     theme.liquid computes body.drop-closed / body.drop-state-* at page load.
     The theme editor (and the Section Rendering API) swap section HTML without
     re-rendering <body>, so a page loaded while the drop was unconfigured
     keeps hiding countdowns even after the merchant configures it. Any section
     carrying data-drop-state re-syncs the body classes with the latest truth. */
  function syncDropBodyClass(scope) {
    var el = (scope || document).querySelector('[data-drop-state]');
    if (!el) return;
    var st = (el.getAttribute('data-drop-state') || '').trim();
    if (!st) return;
    var b = document.body;
    [].slice.call(b.classList).forEach(function (c) {
      if (c.indexOf('drop-state-') === 0) b.classList.remove(c);
    });
    b.classList.add('drop-state-' + st);
    b.classList.toggle('drop-closed', !(st === 'open' || st === 'upcoming'));
  }

  /* ---- public API ---- */
  var api = {
    reduce: reduce,
    marquee: marquee,
    fitBite: fitBite,
    initScope: function (scope) {
      /* re-init inside a reloaded theme-editor section */
      initScramble(scope);
      initReveals(scope);
      initSignatureDraw(scope);
      initFadeImages(scope);
      initCountdowns(scope);
      initMarqueesFromMarkup(scope);
      syncDropBodyClass(scope);
      (scope || document).querySelectorAll('.drop').forEach(fitBite);
    },
    initAll: function () {
      initLoader();
      initWheelDebug();
      initScramble();
      initReveals();
      initSignatureDraw();
      initFadeImages();
      initTopbarScroll();
      initBiteButtons();
      initCountdowns();
      initMarqueesFromMarkup();
      syncDropBodyClass();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', api.initAll);
  else api.initAll();

  /* Shopify theme editor lifecycle */
  document.addEventListener('shopify:section:load', function (e) { api.initScope(e.target); });

  return api;
})();
